import { computed, onMounted, onUnmounted, ref } from 'vue';

import {
  checkAppVersion,
  onAppUpdate,
  startAppVersionPolling,
  type AppVersion,
  type AppUpdateState,
} from '@shared/app-update';

// version.json эмитится только при билде (generateBundle) — на Vite dev-сервере
// запрос уходит в SPA-fallback и возвращает index.html, поэтому поллинг отключён.
const VERSION_POLLING_ENABLED = !import.meta.env.DEV;

export type RemoteVersion = AppVersion;

interface CurrentVersion {
  buildTime: string;
  gitSha: string;
}

/**
 * true, если удалённая версия отличается от текущей забейканной сборки.
 * Битый/пустой ответ (нет строкового `buildTime`) трактуется как «нет новой версии»,
 * чтобы сетевой мусор не взводил баннер.
 */
export function isNewerVersion(remote: RemoteVersion | null | undefined, current: CurrentVersion): boolean {
  if (!remote || typeof remote.buildTime !== 'string') {
    return false;
  }

  if (remote.buildTime !== current.buildTime) {
    return true;
  }

  return typeof remote.gitSha === 'string' && remote.gitSha !== current.gitSha;
}

/**
 * Показывать баннер, если новая версия обнаружена и либо она не отложена,
 * либо это форс-релиз (форс нельзя отложить).
 */
export function shouldShowNotification(state: { detected: boolean; dismissed: boolean; forced: boolean }): boolean {
  return state.detected && (state.forced || !state.dismissed);
}

export const fetchRemoteVersion = checkAppVersion;

/**
 * Composable для отслеживания версии приложения.
 *
 * Когда проверять — решает `startAppVersionPolling` из `@shared/app-update` (обе SPA
 * поллят по одному расписанию); здесь остаётся только состояние баннера: сравнение
 * `buildTime`/`gitSha` с забейканными в бандл значениями. Сетевой сбой баннер не взводит.
 */
export function useAppVersion() {
  // Забейканные при билде значения (Vite `define`) — читаем внутри composable,
  // чтобы module-level импорт (напр. в unit-тестах чистых хелперов) их не требовал.
  const current: CurrentVersion = { buildTime: __APP_BUILD_TIME__, gitSha: __APP_GIT_SHA__ };

  const updateState = ref<AppUpdateState>('ready');
  const maintenanceActionLost = ref(false);
  onUnmounted(
    onAppUpdate((state, actionLost) => {
      updateState.value = state;
      maintenanceActionLost.value = actionLost;
    }),
  );
  const detected = ref(false);
  const dismissed = ref(false);
  const isForced = ref(false);
  // buildTime последней обнаруженной новой версии — чтобы сбросить dismiss при следующем релизе.
  let lastDetectedBuildTime: string | null = null;

  // Пока интерфейс закрыт экраном обновления или техработ, баннер не показываем.
  const hasNewVersion = computed(
    () =>
      updateState.value === 'ready' &&
      shouldShowNotification({ detected: detected.value, dismissed: dismissed.value, forced: isForced.value }),
  );

  const applyRemote = (remote: RemoteVersion): void => {
    if (!isNewerVersion(remote, current)) {
      return;
    }

    // Свежая (по buildTime) версия — сбрасываем прежний dismiss, чтобы баннер показался снова.
    if (remote.buildTime !== lastDetectedBuildTime) {
      lastDetectedBuildTime = remote.buildTime ?? null;
      dismissed.value = false;
    }

    detected.value = true;
    isForced.value = remote.force === true;
  };

  const reloadApp = (): void => {
    window.location.reload();
  };

  const dismiss = (): void => {
    if (!isForced.value) {
      dismissed.value = true;
    }
  };

  if (VERSION_POLLING_ENABLED) {
    let stopPolling: (() => void) | null = null;
    onMounted(() => {
      stopPolling = startAppVersionPolling(applyRemote);
    });
    onUnmounted(() => stopPolling?.());
  }

  return {
    updateState,
    maintenanceActionLost,
    hasNewVersion,
    isForced,
    reloadApp,
    dismiss,
  };
}
